import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const usePortfolio = () => {
  const { token } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [summary, setSummary] = useState(null);
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchPortfolio = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const [portfolioRes, summaryRes, validationRes] = await Promise.all([
        axios.get(`${API}/portfolio/`, { headers }),
        axios.get(`${API}/portfolio/summary`, { headers }),
        axios.get(`${API}/portfolio/validation`, { headers })
      ]);
      setPortfolio(portfolioRes.data);
      setSummary(summaryRes.data);
      setValidation(validationRes.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch portfolio');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPortfolio();
  }, [fetchPortfolio]);

  const addHolding = async (symbol, amountVd) => {
    try {
      const response = await axios.post(
        `${API}/portfolio/holdings/add`,
        { symbol, amount_vd: amountVd },
        { headers }
      );
      await fetchPortfolio();
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to add holding');
    }
  };

  const removeHolding = async (symbol, amountVd = null) => {
    try {
      const payload = { symbol };
      if (amountVd !== null) {
        payload.amount_vd = amountVd;
      }
      const response = await axios.post(
        `${API}/portfolio/holdings/remove`,
        payload,
        { headers }
      );
      await fetchPortfolio();
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to remove holding');
    }
  };

  const setPowerPlay = async (symbol, viceSymbol = null) => {
    try {
      const response = await axios.post(
        `${API}/portfolio/power-play`,
        { symbol, vice_symbol: viceSymbol },
        { headers }
      );
      await fetchPortfolio();
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to set power play');
    }
  };

  return {
    portfolio,
    summary,
    validation,
    loading,
    error,
    addHolding,
    removeHolding,
    setPowerPlay,
    refresh: fetchPortfolio
  };
};

export const useInstruments = () => {
  const { token } = useAuth();
  const [instruments, setInstruments] = useState([]);
  const [pricingStatus, setPricingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchInstruments = useCallback(async (type = null) => {
    try {
      setLoading(true);
      // Request up to 500 instruments to get all available
      const url = type 
        ? `${API}/instruments/?type=${type}&limit=500` 
        : `${API}/instruments/?limit=500`;
      const response = await axios.get(url, { headers });
      setInstruments(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch instruments');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const searchInstruments = async (query) => {
    try {
      const response = await axios.get(`${API}/instruments/search?q=${query}`, { headers });
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Search failed');
    }
  };

  const fetchPricingStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/instruments/pricing/status`, { headers });
      setPricingStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch pricing status:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchInstruments();
    fetchPricingStatus();
  }, [fetchInstruments, fetchPricingStatus]);

  return {
    instruments,
    pricingStatus,
    loading,
    error,
    fetchInstruments,
    searchInstruments,
    fetchPricingStatus
  };
};

export const useLeaderboard = () => {
  const { token } = useAuth();
  const [globalLeaderboard, setGlobalLeaderboard] = useState([]);
  const [weeklyLeaderboard, setWeeklyLeaderboard] = useState([]);
  const [myRanking, setMyRanking] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchLeaderboard = useCallback(async () => {
    try {
      setLoading(true);
      const [globalRes, statsRes] = await Promise.all([
        axios.get(`${API}/leaderboard/global`, { headers }),
        axios.get(`${API}/leaderboard/stats`)
      ]);
      setGlobalLeaderboard(globalRes.data);
      setStats(statsRes.data);

      if (token) {
        try {
          const myRankRes = await axios.get(`${API}/leaderboard/me`, { headers });
          setMyRanking(myRankRes.data);
        } catch {
          // User may not have scores yet
        }
      }
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch leaderboard');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  return {
    globalLeaderboard,
    weeklyLeaderboard,
    myRanking,
    stats,
    loading,
    error,
    refresh: fetchLeaderboard
  };
};

export const useScoring = () => {
  const { token } = useAuth();
  const [myScore, setMyScore] = useState(null);
  const [seasonScore, setSeasonScore] = useState(null);
  const [weekInfo, setWeekInfo] = useState(null);
  const [topPerformers, setTopPerformers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchScoring = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch week info (public)
      const weekInfoRes = await axios.get(`${API}/scoring/week-info`);
      setWeekInfo(weekInfoRes.data);
      
      // Fetch top performers (public)
      const topRes = await axios.get(`${API}/scoring/top-performers`);
      setTopPerformers(topRes.data.top_performers || []);

      if (token) {
        // Fetch user's score
        try {
          const scoreRes = await axios.get(`${API}/scoring/my-score`, { headers });
          setMyScore(scoreRes.data);
        } catch {
          // User may not have score yet
        }

        // Fetch season score
        try {
          const seasonRes = await axios.get(`${API}/scoring/my-season`, { headers });
          setSeasonScore(seasonRes.data);
        } catch {
          // User may not have season score yet
        }
      }
      
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch scoring data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const calculateScore = async () => {
    if (!token) return null;
    try {
      const response = await axios.post(`${API}/scoring/calculate`, {}, { headers });
      setMyScore(response.data);
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to calculate score');
    }
  };

  useEffect(() => {
    fetchScoring();
  }, [fetchScoring]);

  return {
    myScore,
    seasonScore,
    weekInfo,
    topPerformers,
    loading,
    error,
    calculateScore,
    refresh: fetchScoring
  };
};

export const useChips = () => {
  const { token } = useAuth();
  const [chipStatus, setChipStatus] = useState(null);
  const [chipInfo, setChipInfo] = useState(null);
  const [activeChip, setActiveChip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchChips = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch chip info (public)
      const infoRes = await axios.get(`${API}/chips/info`);
      setChipInfo(infoRes.data);

      if (token) {
        // Fetch user's chip status
        const statusRes = await axios.get(`${API}/chips/status`, { headers });
        setChipStatus(statusRes.data);
        
        // Fetch active chip
        const activeRes = await axios.get(`${API}/chips/active`, { headers });
        setActiveChip(activeRes.data);
      }
      
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch chips data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const activateChip = async (chipType, targetSymbol = null) => {
    if (!token) return null;
    try {
      const payload = { chip_type: chipType };
      if (targetSymbol) {
        payload.target_symbol = targetSymbol;
      }
      const response = await axios.post(`${API}/chips/activate`, payload, { headers });
      await fetchChips();
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to activate chip');
    }
  };

  const deactivateChip = async () => {
    if (!token) return null;
    try {
      const response = await axios.post(`${API}/chips/deactivate`, {}, { headers });
      await fetchChips();
      return response.data;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to deactivate chip');
    }
  };

  useEffect(() => {
    fetchChips();
  }, [fetchChips]);

  return {
    chipStatus,
    chipInfo,
    activeChip,
    loading,
    error,
    activateChip,
    deactivateChip,
    refresh: fetchChips
  };
};

export const useGameInfo = () => {
  const [gameInfo, setGameInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGameInfo = async () => {
      try {
        const response = await axios.get(`${API}/game-info`);
        setGameInfo(response.data);
      } catch (err) {
        console.error('Failed to fetch game info:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchGameInfo();
  }, []);

  return { gameInfo, loading };
};
